import { createApi } from "@reduxjs/toolkit/query/react";

import type {
  IAddLeaveTypeRequest,
  TAddLeaveTypeResponse,
  TGetLeaveTypeByIdRequest,
  TGetLeaveTypeByIdResponse,
  TGetLeaveTypesListingRequest,
  TGetLeaveTypesListingResponse,
  TupdateLeaveTypeRequest,
  TUpdateLeaveTypeResponse,
} from "@/types/(company)/leaveTypes.interface";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { APIEndpoints } from "@/constants/apiEndpoints";
import { companyLeaveEntitlementsApi } from "./leavesEntitlementsApi";
import { companyCarryForwardPoliciesApi } from "./carryForwardPoliciesApi";
import { companyEnchashmentPolicyApi } from "./enchashmentPolicyApi";

export const companyLeaveTypesApi = createApi({
  reducerPath: "companyLeaveTypesApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["getAllLeaveTypes"],
  endpoints: (builder) => ({
    getAllLeaveTypes: builder.query<
      TGetLeaveTypesListingResponse,
      TGetLeaveTypesListingRequest
    >({
      query: () => {
        return {
          url: "/company/settings/leave-types",
          method: "GET",
        };
      },
      providesTags: ["getAllLeaveTypes"],
    }),
    getPaginatedLeaveTypes: builder.infiniteQuery<
      TGetLeaveTypesListingResponse,
      { limit: number; search?: string },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage) =>
          lastPage.meta.hasNextPage ? lastPage.meta.page + 1 : undefined,
      },
      query: ({ queryArg, pageParam }) => ({
        url: APIEndpoints.Company.settings.getPaginatedLeaveTypes,
        method: "GET",
        params: {
          page: pageParam,
          limit: queryArg.limit ?? 10,
          search: queryArg.search,
        },
      }),
      providesTags: ["getAllLeaveTypes"],
    }),
    addLeaveType: builder.mutation<TAddLeaveTypeResponse, IAddLeaveTypeRequest>(
      {
        query: (data) => {
          return {
            url: "/company/settings/leave-types",
            method: "POST",
            body: data,
          };
        },
        invalidatesTags: ["getAllLeaveTypes"],
      },
    ),
    getLeaveTypeById: builder.query<
      TGetLeaveTypeByIdResponse,
      TGetLeaveTypeByIdRequest
    >({
      query: ({ id }) => {
        return {
          url: "/company/settings/leave-types" + `/${id}`,
          method: "GET",
        };
      },
      providesTags: ["getAllLeaveTypes"],
    }),
    updateLeaveType: builder.mutation<
      TUpdateLeaveTypeResponse,
      TupdateLeaveTypeRequest
    >({
      query: (data) => {
        return {
          url: "/company/settings/leave-types" + "/" + data.id,
          method: "PATCH",
          body: data.data,
        };
      },
      invalidatesTags: ["getAllLeaveTypes"],
    }),
    // 204 No Content: resolves with no body.
    deleteLeaveType: builder.mutation<void, number>({
      query: (id) => ({
        url: "/company/settings/leave-types" + `/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["getAllLeaveTypes"],
      // The API deletes the type's entitlement with it, and that cache lives
      // in a separate createApi slice, which tags can't reach.
      async onQueryStarted(_id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            companyLeaveEntitlementsApi.util.invalidateTags([
              "LeaveEntitlementsSettings",
            ]),
          );
        } catch {
          // request failed — nothing to invalidate
        }
      },
    }),
    // Deletes the type's carry-forward AND encashment policies atomically on
    // the server — replaces two client-side deletes that could half-succeed.
    // 204 No Content.
    deleteLeaveTypePolicies: builder.mutation<void, number>({
      query: (id) => ({
        url: `/company/settings/leave-types/${id}/policies`,
        method: "DELETE",
      }),
      // Both caches live in other createApi slices, which tags can't reach.
      async onQueryStarted(_id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            companyCarryForwardPoliciesApi.util.invalidateTags([
              "CarryForwardSettings",
            ]),
          );
          dispatch(
            companyEnchashmentPolicyApi.util.invalidateTags(["LeaveEncashments"]),
          );
        } catch {
          // request failed — nothing to invalidate
        }
      },
    }),
  }),
});

export const {
  useGetAllLeaveTypesQuery,
  useDeleteLeaveTypeMutation,
  useDeleteLeaveTypePoliciesMutation,
  useGetLeaveTypeByIdQuery,
  useUpdateLeaveTypeMutation,
  useAddLeaveTypeMutation,
  useGetPaginatedLeaveTypesInfiniteQuery,
} = companyLeaveTypesApi;
